// The "Now" card: what to do on this store, the words to say, and the
// outcome buttons that move the row. Used inside the store drawer and on
// the Playbook's run card. The parent does the saving; this card owns the
// small prompts (why lost, what number, when is the demo, the deal) and
// the note box with dictation and AI tidy-up.
//
// With `hotkeys` on, the number keys 1..9 pick an outcome when no field has
// focus, so a rep on a laptop can dial, talk, press a key, next.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Mic, MicOff, Sparkles, MessageSquare } from 'lucide-react';
import { DO_NOT_CONTACT, currentAction, dueBucket, dueLabel, fillScript, isDoNotContact, phaseOf, smsLink, whereItGoes } from './salesPlan';
import { e164 } from './salesData';
import { assist, prospectForAssist, speechSupported, startDictation } from './salesAssist';
import { btnGhost, btnPrimary, inputCls } from './salesUi';
import DealForm, { dealFromProspect, dealPatch, dealValid } from './DealForm';

export const outcomeTone = (next) =>
  next === 'demo' || next === 'pilot' || next === 'customer' || next === 'restart'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
    : next === 'lost'
      ? 'border-red-200 bg-white text-red-700 hover:bg-red-50'
      : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50';

const typing = () => {
  const el = document.activeElement;
  const tag = el?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable;
};

// datetime-local wants local "YYYY-MM-DDTHH:MM"
const localInput = (d) => {
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
};
const defaultWhen = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 30, 0, 0); return localInput(d); };

/**
 * onOutcome(outcome, noteText, extraPatch, { when }) -> Promise<row | null>.
 * The card clears its prompts when a row comes back. An outcome that makes
 * the store a customer asks for the deal first (when the deal columns
 * exist); one that books a demo asks for the time.
 * onFacts(facts) is offered the facts the tidy-up pulled out of the note.
 * `plain` drops the step title, goal and script hint (the Arena shows its own).
 */
export default function ActionCard({ prospect: p, me, busy, onOutcome, note, setNote, hotkeys = false, tone = 'blue', eyebrow = 'Now', dealsReady = false, reps = [], lockedBy = null, client = null, storeId = '', onFacts = null, hideScript = false, inbound = null, plain = false, touches = [], showPhase = true, showPath = true }) {
  const [unlocked, setUnlocked] = useState(false);
  const [copied, setCopied] = useState('');
  const [pendingLost, setPendingLost] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [never, setNever] = useState(false);
  const [pendingHold, setPendingHold] = useState(null);
  const [holdText, setHoldText] = useState('');
  const [pendingDeal, setPendingDeal] = useState(null);
  const [deal, setDeal] = useState(() => dealFromProspect(p, { me }));
  const [pendingWhen, setPendingWhen] = useState(null);
  const [when, setWhen] = useState(defaultWhen);
  const [ownerName, setOwnerName] = useState('');
  // dictation and tidy
  const [listening, setListening] = useState(false);
  const stopRef = useRef(null);
  const baseRef = useRef('');
  const [tidying, setTidying] = useState(false);
  const [tidy, setTidy] = useState(null); // { facts, suggested_outcome, confidence }
  const [tidyError, setTidyError] = useState('');

  const action = useMemo(() => currentAction(p), [p]);
  const phase = useMemo(() => (showPhase ? phaseOf(p, touches) : null), [p, touches, showPhase]);
  useEffect(() => {
    setPendingLost(null); setPendingHold(null); setPendingDeal(null); setPendingWhen(null); setUnlocked(false);
    setDeal(dealFromProspect(p, { me })); setTidy(null); setTidyError(''); setOwnerName('');
    if (stopRef.current) { stopRef.current(); stopRef.current = null; setListening(false); }
  }, [p?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (stopRef.current) stopRef.current(); }, []);
  const locked = !!lockedBy && !unlocked;

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ }
  };

  const log = async (o, extra = '') => {
    if (busy || !o || locked) return;
    if (o.next === 'lost' && pendingLost?.key !== o.key) { setPendingLost(o); setLostReason(''); setNever(false); setPendingHold(null); setPendingDeal(null); setPendingWhen(null); return; }
    if (o.ask && pendingHold?.key !== o.key) { setPendingHold(o); setHoldText(''); setPendingLost(null); setPendingDeal(null); setPendingWhen(null); return; }
    if (o.askDate && pendingWhen?.key !== o.key) { setPendingWhen(o); setWhen(p.demo_at && o.toStep === 0 && o.next === 'demo_step' ? localInput(p.demo_at) : defaultWhen()); setPendingLost(null); setPendingHold(null); setPendingDeal(null); return; }
    if (o.next === 'customer' && dealsReady && pendingDeal?.key !== o.key) { setPendingDeal(o); setDeal(dealFromProspect(p, { me })); setPendingLost(null); setPendingHold(null); setPendingWhen(null); return; }
    const text = [String(note || '').trim(), String(extra || '').trim()].filter(Boolean).join(' · ');
    let patch = o.next === 'customer' && dealsReady ? dealPatch(deal) : undefined;
    if (o.next === 'lost' && never) {
      const tags = Array.isArray(p.tags) ? p.tags : [];
      patch = { ...(patch || {}), tags: tags.includes(DO_NOT_CONTACT) ? tags : [...tags, DO_NOT_CONTACT], lost_reason: `Do not contact. ${String(extra || '').trim() || 'They want no software.'}` };
    }
    if (ownerName.trim() && !p.owner_name) patch = { ...(patch || {}), owner_name: ownerName.trim() };
    const whenIso = o.askDate && when ? new Date(when).toISOString() : null;
    const row = await onOutcome(o, text, patch, { when: whenIso, never: o.next === 'lost' && never });
    if (row) { setPendingLost(null); setPendingHold(null); setPendingDeal(null); setPendingWhen(null); setTidy(null); setOwnerName(''); }
  };

  useEffect(() => {
    if (!hotkeys || !action) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing()) return;
      if (pendingLost || pendingHold || pendingDeal || pendingWhen) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > action.outcomes.length) return;
      e.preventDefault();
      log(action.outcomes[n - 1]);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }); // every render: `log` closes over the latest note and prompts

  // ---- dictation ----
  const toggleDictation = () => {
    if (listening) { stopRef.current?.(); stopRef.current = null; setListening(false); return; }
    baseRef.current = String(note || '').trim();
    setListening(true);
    stopRef.current = startDictation(
      (text) => setNote([baseRef.current, text].filter(Boolean).join(' ')),
      () => { setListening(false); stopRef.current = null; },
    );
  };

  // ---- tidy ----
  const runTidy = async () => {
    const raw = String(note || '').trim();
    if (!raw || tidying || !client) return;
    setTidying(true);
    setTidyError('');
    try {
      const res = await assist(client, storeId, 'tidy', {
        raw,
        prospect: prospectForAssist(p),
        step: action?.label || '',
        outcomes: (action?.outcomes || []).map((o) => ({ key: o.key, label: o.label })),
        today: new Date().toISOString(),
      });
      setNote(res.note || raw);
      setTidy(res);
      if (onFacts && res.facts) onFacts(res.facts);
    } catch (e) {
      console.error('sales: tidy failed', e);
      setTidyError(e.message || 'Could not tidy that');
    } finally {
      setTidying(false);
    }
  };

  const script = action ? fillScript(action.say, { prospect: p, rep: me }) : '';
  const vm = action?.voicemail ? fillScript(action.voicemail, { prospect: p, rep: me }) : '';
  const b = dueBucket(p.next_due_at);
  const frame = tone === 'blue' ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white';
  const box = tone === 'blue' ? 'border-blue-100' : 'border-slate-200';
  const tel = e164(p.phone);
  const canDictate = speechSupported();
  const factChips = tidy?.facts ? [
    tidy.facts.owner_name && `Owner: ${tidy.facts.owner_name}`,
    tidy.facts.decision_maker && `Decides: ${tidy.facts.decision_maker}`,
    tidy.facts.best_time && `Best time: ${tidy.facts.best_time}`,
    tidy.facts.interest && `Interest: ${tidy.facts.interest}`,
    tidy.facts.objection && `Objection: ${tidy.facts.objection}`,
    tidy.facts.competitor && `Uses: ${tidy.facts.competitor}`,
    tidy.facts.cell_given && 'Gave their cell',
    tidy.facts.callback_at && `Callback: ${new Date(tidy.facts.callback_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`,
  ].filter(Boolean) : [];

  if (!action) {
    return (
      <div className={`rounded-2xl border p-4 ${frame}`}>
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{eyebrow}</span>
        <h3 className={`text-lg font-bold mt-1 ${isDoNotContact(p) ? 'text-red-700' : 'text-slate-900'}`}>{isDoNotContact(p) ? 'Do not contact' : p.stage === 'lost' ? 'Lost' : 'Nothing due'}</h3>
        {p.lost_reason && <p className="text-sm text-slate-600 mt-1">{p.lost_reason}</p>}
        {isDoNotContact(p) ? <p className="text-xs text-red-700 mt-2">They were clear. No calls, no emails, no visits. The row stays so an import cannot bring them back.</p> : p.stage === 'lost' && <p className="text-xs text-slate-500 mt-2">Changed their mind? Move the stage to Parked or New.</p>}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-4 ${frame}`}>
      <div className={`flex flex-wrap items-center gap-2 ${plain && !action.isReply && !action.isDemo ? 'hidden' : ''}`}>
        <span className={`text-[11px] font-bold uppercase tracking-wider ${tone === 'blue' ? 'text-blue-700' : 'text-slate-500'}`}>{eyebrow}</span>
        {action.isSequence && !plain && <span className="text-[11px] font-bold text-slate-500">Step {action.stepIndex + 1} of {action.stepTotal}</span>}
        {action.isReply && <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold">They replied</span>}
        {action.isDemo && <span className="text-[11px] font-bold text-violet-700">Demo {action.stepIndex + 1} of {action.stepTotal}{p.demo_at ? ` · ${new Date(p.demo_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</span>}
        {p.next_due_at && !plain && <span className={`ml-auto px-2 py-0.5 rounded-full border text-[11px] font-bold ${b === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' : b === 'today' ? 'bg-white text-blue-700 border-blue-200' : 'bg-white text-slate-600 border-slate-200'}`}>{dueLabel(p.next_due_at)}</span>}
      </div>
      {phase && (
        <div className={`mt-2 rounded-lg border px-3 py-2 ${phase.tone === 'emerald' ? 'border-emerald-200 bg-emerald-50' : phase.tone === 'violet' ? 'border-violet-200 bg-violet-50' : phase.tone === 'amber' ? 'border-amber-200 bg-amber-50' : phase.tone === 'red' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`} title="Where this store is in the flow">
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-500">You are here</span>
          <div className="text-sm"><span className="font-bold text-slate-900">{phase.title}.</span> <span className="text-slate-600">{phase.detail}</span></div>
        </div>
      )}
      {!plain && <h3 className="text-lg font-bold text-slate-900 mt-1">{action.label}</h3>}
      {!plain && <p className="text-sm text-slate-600 mt-1">{action.goal}</p>}
      {action.isReply && inbound && (
        <blockquote className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">They wrote{inbound.created_at ? ` · ${new Date(inbound.created_at).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}</div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{String(inbound.body_text || '').trim().slice(0, 700) || '(no text)'}{String(inbound.body_text || '').trim().length > 700 ? '…' : ''}</p>
        </blockquote>
      )}
      {hideScript && !plain && <p className="mt-2 text-xs text-sky-700">{action.isReply ? 'Answer in the email above. Sending it logs this step and puts the store back on its plan.' : 'The email above is this step. Send it there, and the row moves on by itself.'}</p>}
      {script && !hideScript && (
        <div className="mt-3 relative">
          <pre className={`whitespace-pre-wrap font-sans text-sm text-slate-800 bg-white border rounded-lg px-3 py-2.5 pr-12 ${box}`}>{script}</pre>
          <button type="button" onClick={() => copy('say', script)} className="absolute top-2 right-2 w-8 h-8 rounded-md bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" title="Copy">{copied === 'say' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
        </div>
      )}
      {action.kind === 'text' && tel && !plain && (
        <a href={smsLink(tel, script)} className="mt-2 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold"><MessageSquare className="w-4 h-4" /> Open in Messages with this text</a>
      )}
      {vm && !plain && (
        <div className="mt-2 relative">
          <pre className={`whitespace-pre-wrap font-sans text-sm text-slate-800 bg-white border rounded-lg px-3 py-2.5 pr-12 ${box}`}><span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">If voicemail</span>{vm}</pre>
          <button type="button" onClick={() => copy('vm', vm)} className="absolute top-2 right-2 w-8 h-8 rounded-md bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500" title="Copy">{copied === 'vm' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
        </div>
      )}
      {locked && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex flex-wrap items-center gap-2">
          <span><span className="font-semibold">{lockedBy.name}'s store.</span> One voice per store, so leave this one to them. Reassign it above if it should be yours.</span>
          <button type="button" onClick={() => setUnlocked(true)} className="ml-auto text-xs font-semibold underline">I already talked to them, log it anyway</button>
        </div>
      )}

      {!p.owner_name && action.kind === 'call' && !locked && (
        <div className="mt-3 flex items-center gap-2">
          <label htmlFor="sales-owner-name" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">Owner's name</label>
          <input id="sales-owner-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Ask whoever picks up. Every script after this uses it." className={`${inputCls} h-9`} />
        </div>
      )}

      {/* the note: typed or dictated, then tidied */}
      <div className={plain ? 'mt-0' : 'mt-3'}>
        <div className="relative">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} style={plain ? { minHeight: 36, paddingTop: 7, paddingBottom: 7 } : undefined} placeholder={plain ? 'What they said (optional)' : canDictate ? 'What they said. Type it, or tap the mic and talk.' : 'What they said (optional)'} rows={note && note.length > 80 ? 3 : 1} className={`${inputCls} pr-24 resize-none ${listening ? 'border-red-400 ring-2 ring-red-500/20' : ''}`} disabled={locked} />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {canDictate && (
              <button type="button" onClick={toggleDictation} disabled={locked} className={`w-8 h-8 rounded-md flex items-center justify-center ${listening ? 'bg-red-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'}`} title={listening ? 'Stop' : 'Dictate'}>
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            {client && (
              <button type="button" onClick={runTidy} disabled={locked || tidying || !String(note || '').trim()} className="w-8 h-8 rounded-md bg-white border border-slate-200 text-violet-600 hover:bg-violet-50 flex items-center justify-center disabled:opacity-40" title="Tidy the note and pull out the facts">
                <Sparkles className={`w-4 h-4 ${tidying ? 'animate-pulse' : ''}`} />
              </button>
            )}
          </div>
        </div>
        {listening && <div className="mt-1 text-[11px] text-red-600 font-semibold">Listening. Tap the mic again to stop.</div>}
        {tidyError && <div className="mt-1 text-[11px] text-red-600">{tidyError}</div>}
        {factChips.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {factChips.map((c) => <span key={c} className="px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-[11px] font-semibold text-violet-900">{c}</span>)}
            {tidy?.suggested_outcome && <span className="text-[11px] text-slate-500 self-center">Sounds like: <span className="font-semibold">{action.outcomes.find((o) => o.key === tidy.suggested_outcome)?.label}</span></span>}
          </div>
        )}
      </div>

      <div className={`${plain ? 'mt-1.5' : 'mt-2'} flex items-center justify-between`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" title="Pick what happened. The grey line under each one is where the store goes next.">What happened?</span>
        {hotkeys && <span className="hidden sm:inline text-[11px] text-slate-400">Press the number key</span>}
      </div>
      <div className={`mt-1.5 grid gap-1.5 ${plain ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 gap-2'}`}>
        {action.outcomes.map((o, i) => {
          const suggested = tidy?.suggested_outcome === o.key;
          const active = pendingLost?.key === o.key || pendingHold?.key === o.key || pendingDeal?.key === o.key || pendingWhen?.key === o.key;
          return (
            <button key={o.key} type="button" disabled={busy || locked} onClick={() => log(o)} className={`text-left rounded-lg border font-semibold transition-colors disabled:opacity-50 flex items-center ${plain ? 'px-2 py-1.5 text-[12px] gap-2' : 'px-3 py-2.5 text-sm gap-2.5'} ${outcomeTone(o.next)} ${active ? 'ring-2 ring-blue-500/30' : ''} ${suggested ? 'ring-2 ring-violet-500/40' : ''}`}>
              {hotkeys && <span className="hidden sm:flex w-5 h-5 rounded border border-current/30 text-[11px] font-bold items-center justify-center opacity-60 flex-shrink-0">{i + 1}</span>}
              <span className="min-w-0 flex-1">
                <span className={`block leading-tight ${plain ? 'line-clamp-2' : ''}`} title={o.label}>{o.label}</span>
                {showPath && <span className={`block font-normal text-slate-500 leading-tight mt-0.5 ${plain ? 'text-[10px] truncate' : 'text-[11px]'}`} title={whereItGoes(p, o)}>{whereItGoes(p, o)}</span>}
              </span>
              {suggested && <Sparkles className="w-3.5 h-3.5 text-violet-600 ml-auto flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {pendingLost && (
        <div className="mt-3 bg-white border border-red-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-red-700">Why did they say no?</div>
          {pendingLost.circleBack && <p className="text-xs text-slate-500 mt-0.5">The store parks and comes back in {pendingLost.circleBack} days. A first no is usually a reflex.</p>}
          <input value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Price, has a site already, not the owner…" className={`${inputCls} mt-2`} autoFocus onKeyDown={(e) => e.key === 'Enter' && log(pendingLost, lostReason)} />
          <label className="mt-2 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} className="mt-0.5" />
            <span><span className="font-semibold">They were very clear: never contact them again.</span> Marks the store do-not-contact. No revisit, nobody calls.</span>
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setPendingLost(null)} className={btnGhost}>Back</button>
            <button type="button" disabled={busy} onClick={() => log(pendingLost, lostReason)} className="inline-flex items-center h-10 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">{pendingLost.circleBack && !never ? `Park ${pendingLost.circleBack} days` : 'Mark lost'}</button>
          </div>
        </div>
      )}
      {pendingWhen && (
        <div className="mt-3 bg-white border border-violet-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-violet-800">{pendingWhen.askDate}</div>
          <p className="text-xs text-slate-500 mt-0.5">{pendingWhen.callback ? 'The store comes back to the top of your queue at that time. Call on time and it is worth 5 extra points.' : 'The day before, a reminder text comes up as the next step. Reminders cut no-shows by about a quarter.'}</p>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={`${inputCls} mt-2`} autoFocus />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setPendingWhen(null)} className={btnGhost}>Back</button>
            <button type="button" disabled={busy || !when} onClick={() => log(pendingWhen)} className={btnPrimary}>Save the time</button>
          </div>
        </div>
      )}
      {pendingDeal && (
        <div className="mt-3 bg-white border border-emerald-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-emerald-800">The deal. This is what the commission is paid on.</div>
          <div className="mt-3"><DealForm value={deal} onChange={setDeal} reps={reps} compact /></div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setPendingDeal(null)} className={btnGhost}>Back</button>
            <button type="button" disabled={busy || !dealValid(deal)} onClick={() => log(pendingDeal)} className="inline-flex items-center h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">Mark as customer</button>
          </div>
        </div>
      )}
      {pendingHold && (
        <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-slate-800">{pendingHold.ask}</div>
          <input value={holdText} onChange={(e) => setHoldText(e.target.value)} className={`${inputCls} mt-2`} autoFocus onKeyDown={(e) => e.key === 'Enter' && log(pendingHold, holdText)} />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setPendingHold(null)} className={btnGhost}>Back</button>
            <button type="button" disabled={busy} onClick={() => log(pendingHold, holdText)} className={btnPrimary}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
